import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Folder, Star } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import SetInitialPasswordModal from '../components/SetInitialPasswordModal';
import '../styles/Workspace.css';
import '../styles/Buttons.css';

const Workspaces: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout, refreshUser } = useAuth();
  const { workspaces, loading, fetchWorkspaces, createWorkspace, selectWorkspace } = useWorkspace();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState('');
  const [newWorkspaceName, setNewWorkspaceName] = useState('');
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  useEffect(() => {
    fetchWorkspaces().catch((error) => {
      console.error('Failed to fetch workspaces:', error);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Check if user requires password change
    if (user?.requirePasswordChange) {
      setShowPasswordModal(true);
    }
  }, [user]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleCreateWorkspace = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newWorkspaceName.trim()) {
      setCreateError('Workspace name is required');
      return;
    }

    try {
      setCreateLoading(true);
      setCreateError('');
      const workspace = await createWorkspace({ name: newWorkspaceName.trim() });
      setShowCreateModal(false);
      setNewWorkspaceName('');
      // Optionally, select the newly created workspace
      selectWorkspace(workspace);
    } catch (error: any) {
      setCreateError(error.response?.data?.message || 'Failed to create workspace');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleWorkspaceClick = (workspace: any) => {
    selectWorkspace(workspace);
    // Navigate to builders module
    navigate(`/workspaces/${workspace._id}/builders`);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((word) => word[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  return (
    <div className="workspace-page">
      {/* Header */}
      <header className="workspace-header">
        <div className="workspace-header-content">
          <div className="workspace-header-logo">
            <img src="/logo-light.png" alt="EnHomeBuilder" style={{ height: '60px' }} />
          </div>
          <div className="workspace-header-user">
            <span className="workspace-header-name">{user?.name}</span>
            <button className="workspace-header-logout" onClick={handleLogout}>
              Sign out
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="workspace-main">
        <div className="workspace-container">
          {/* Section Header */}
          <div className="workspace-section-header">
            <h1 className="workspace-section-title">Your Workspaces</h1>
            <p className="workspace-section-subtitle">
              Select a workspace to continue or create a new one
            </p>
          </div>

          {/* Actions */}
          <div className="workspace-actions">
            <button
              className="btn btn-primary btn-md"
              onClick={() => setShowCreateModal(true)}
            >
              + Create Workspace
            </button>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="workspace-loading">
              <div className="workspace-loading-spinner" />
            </div>
          )}

          {/* Empty State */}
          {!loading && workspaces.length === 0 && (
            <div className="workspace-empty">
              <div className="workspace-empty-icon">
                <Folder size={48} color="#6B7280" />
              </div>
              <h2 className="workspace-empty-title">No workspaces yet</h2>
              <p className="workspace-empty-text">
                Get started by creating your first workspace
              </p>
              <button
                className="btn btn-primary btn-lg"
                onClick={() => setShowCreateModal(true)}
              >
                Create your first workspace
              </button>
            </div>
          )}

          {/* Workspace Grid */}
          {!loading && workspaces.length > 0 && (
            <div className="workspace-grid">
              {workspaces.map((workspace) => (
                <div
                  key={workspace._id}
                  className="workspace-card"
                  onClick={() => handleWorkspaceClick(workspace)}
                >
                  <div className="workspace-card-header">
                    <div className="workspace-card-logo">
                      {workspace.logo ? (
                        <img src={workspace.logo} alt={workspace.name} />
                      ) : (
                        getInitials(workspace.name)
                      )}
                    </div>
                    <div className="workspace-card-info">
                      <h3 className="workspace-card-name">{workspace.name}</h3>
                      <span className="workspace-card-plan">
                        {workspace.plan || 'Free'}
                      </span>
                    </div>
                  </div>
                  <div className="workspace-card-meta">
                    {workspace.role && (
                      <span className="workspace-card-role">{workspace.role}</span>
                    )}
                    {workspace.isDefault && (
                      <span className="workspace-card-default">
                        <Star size={14} fill="currentColor" /> Default
                      </span>
                    )}
                  </div>
                  <div className="workspace-card-date">
                    Created {formatDate(workspace.createdAt)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="workspace-footer">
        Powered by EnHomeBuilder
      </footer>

      {/* Set Initial Password Modal */}
      <SetInitialPasswordModal
        isOpen={showPasswordModal}
        onSuccess={async () => {
          setShowPasswordModal(false);
          // Refresh user data to clear the requirePasswordChange flag
          if (refreshUser) {
            await refreshUser();
          }
        }}
      />

      {/* Create Workspace Modal */}
      {showCreateModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '24px',
          }}
          onClick={() => setShowCreateModal(false)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '8px',
              padding: '32px',
              maxWidth: '480px',
              width: '100%',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2
              style={{
                fontSize: '20px',
                fontWeight: 600,
                color: '#111827',
                marginBottom: '8px',
              }}
            >
              Create New Workspace
            </h2>
            <p
              style={{
                fontSize: '14px',
                color: '#6B7280',
                marginBottom: '24px',
              }}
            >
              Enter a name for your new workspace
            </p>

            <form onSubmit={handleCreateWorkspace}>
              {createError && (
                <div className="form-error" style={{ marginBottom: '16px' }}>
                  {createError}
                </div>
              )}

              <div className="form-group" style={{ marginBottom: '24px' }}>
                <label htmlFor="workspaceName" className="form-label">
                  Workspace name
                </label>
                <input
                  type="text"
                  id="workspaceName"
                  className="form-input"
                  placeholder="My Workspace"
                  value={newWorkspaceName}
                  onChange={(e) => setNewWorkspaceName(e.target.value)}
                  disabled={createLoading}
                  autoFocus
                />
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="btn btn-secondary btn-md"
                  onClick={() => setShowCreateModal(false)}
                  disabled={createLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={`btn btn-primary btn-md ${createLoading ? 'btn-loading' : ''}`}
                  disabled={createLoading}
                >
                  {createLoading ? '' : 'Create Workspace'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Workspaces;
